// The moving dots. One dot is not one request: the page animates the RATE a line
// carries, so a busier line shows more dots, and a failing one shows dots that
// fade before they arrive. The field is plain data stepped by time, so it can be
// tested without a browser.

import { cubicPoint, type Cubic, type Pt } from './geometry.js';
import type { EdgeState } from './map-model.js';

export interface DotEdge {
  readonly id: string;
  readonly curve: Cubic;
  readonly dps: number;
  readonly state: EdgeState;
  readonly drop: boolean;
}

export interface Particle {
  readonly edgeId: string;
  /** 0 at the start of the line, 1 at its end. */
  t: number;
  /** Fractions of the line per second. */
  readonly v: number;
}

export interface DotPlace {
  readonly x: number;
  readonly y: number;
  readonly state: EdgeState;
  readonly opacity: number;
}

const SECONDS_TO_CROSS = { ok: 3.4, warn: 4.6, bad: 5.6, idle: 3.4 } as const;
/** A failing line's dots stop short of the end. */
export const DROP_AT = 0.8;

export class ParticleField {
  readonly parts: Particle[] = [];
  private readonly acc = new Map<string, number>();

  constructor(
    private readonly max = 170,
    private readonly rand: () => number = Math.random,
  ) {}

  private spawn(e: DotEdge, at: number): void {
    if (this.parts.length >= this.max) return;
    const crossing = SECONDS_TO_CROSS[e.state] * (0.9 + this.rand() * 0.2);
    this.parts.push({ edgeId: e.id, t: at, v: 1 / crossing });
  }

  /** Advance every dot by `dt` seconds and start new ones at each line's rate. */
  step(edges: readonly DotEdge[], dt: number): void {
    const byId = new Map(edges.map((e) => [e.id, e]));
    for (const e of edges) {
      if (e.state === 'idle') {
        this.acc.delete(e.id);
        continue;
      }
      let a = (this.acc.get(e.id) ?? 0) + e.dps * dt;
      while (a >= 1) {
        a -= 1;
        this.spawn(e, 0);
      }
      this.acc.set(e.id, a);
    }
    for (let i = this.parts.length - 1; i >= 0; i -= 1) {
      const p = this.parts[i]!;
      const e = byId.get(p.edgeId);
      if (!e || e.state === 'idle') {
        this.parts.splice(i, 1);
        continue;
      }
      p.t += p.v * dt;
      if (p.t >= (e.drop ? DROP_AT : 1)) this.parts.splice(i, 1);
    }
  }

  /** Dots already along each line at the start, so the page is not empty for 3 seconds. */
  prefill(edges: readonly DotEdge[]): void {
    for (const e of edges) {
      if (e.state === 'idle') continue;
      const n = Math.ceil(e.dps * SECONDS_TO_CROSS[e.state]);
      for (let i = 0; i < n; i += 1) this.spawn(e, this.rand() * (e.drop ? DROP_AT : 1));
    }
  }

  /** For people who ask for less motion: a few fixed dots per busy line, no movement. */
  place(edges: readonly DotEdge[]): void {
    this.parts.length = 0;
    this.acc.clear();
    for (const e of edges) {
      if (e.state === 'idle') continue;
      const n = Math.max(1, Math.round(e.dps * 2));
      for (let i = 0; i < n; i += 1) {
        this.spawn(e, ((i + 0.5) / n) * (e.drop ? DROP_AT : 1));
      }
    }
  }

  /** Where to draw every dot now. */
  places(edges: readonly DotEdge[]): DotPlace[] {
    const byId = new Map(edges.map((e) => [e.id, e]));
    const out: DotPlace[] = [];
    for (const p of this.parts) {
      const e = byId.get(p.edgeId);
      if (!e) continue;
      const pt: Pt = cubicPoint(e.curve, Math.min(1, p.t));
      const fading = e.drop && p.t > 0.6 ? Math.max(0, (DROP_AT - p.t) / (DROP_AT - 0.6)) : 1;
      out.push({ x: pt.x, y: pt.y, state: e.state, opacity: fading });
    }
    return out;
  }
}
